/**
 * Pure VideoJS player component.
 * Receives all data pre-resolved — does NOT fetch anything.
 * @module components/manage/VideoJS/VideoJS
 */
import { useEffect, useRef } from 'react';
import videojs from 'video.js';
import 'videojs-hls-quality-selector';
import 'videojs-contrib-quality-levels';
import './plugins/sprite-thumbnails.js';
import './plugins/videojs-hotkeys.js';
import { LINKED_TITLE_BAR } from './LinkedTitleBar';
import './video-js.css';
import 'videojs-awesome-watermark';
import 'videojs-awesome-watermark/dist/videojs-awesome-watermark.css';
import config from '@plone/volto/registry';
import { v4 as uuid } from 'uuid';
import cx from 'classnames';
import { DEFAULT_VHS_OPTIONS, pinDefaultQuality } from './videoJsOptions';

// Finnish translations for player UI
const FI_TRANSLATIONS = {
  Play: 'Toisto',
  Pause: 'Tauko',
  'Current Time': 'Tämänhetkinen aika',
  Duration: 'Kokonaisaika',
  'Remaining Time': 'Jäljellä oleva aika',
  'Stream Type': 'Lähetystyyppi',
  LIVE: 'LIVE',
  Loaded: 'Ladattu',
  Progress: 'Edistyminen',
  Fullscreen: 'Koko näyttö',
  'Non-Fullscreen': 'Koko näyttö pois',
  Mute: 'Ääni pois',
  Unmute: 'Ääni päällä',
  'Playback Rate': 'Toistonopeus',
  Subtitles: 'Tekstitys',
  'subtitles off': 'Tekstitys pois',
  Captions: 'Tekstitys',
  'captions off': 'Tekstitys pois',
  Chapters: 'Kappaleet',
  'You aborted the media playback': 'Olet keskeyttänyt videotoiston.',
  'A network error caused the media download to fail part-way.':
    'Verkkovirhe keskeytti videon latauksen.',
  'The media could not be loaded, either because the server or network failed or because the format is not supported.':
    'Videon lataus ei onnistunut joko palvelin- tai verkkovirheestä tai väärästä formaatista johtuen.',
  'The media playback was aborted due to a corruption problem or because the media used features your browser did not support.':
    'Videon toisto keskeytyi, koska media on vaurioitunut tai käyttää käyttää toimintoja, joita selaimesi ei tue.',
  'No compatible source was found for this media.':
    'Tälle videolle ei löytynyt yhteensopivaa lähdettä.',
};

// Filter options to prevent XSS from untrusted block data
const filterOptions = (options) => {
  if (!options) return {};
  return Object.keys(options)
    .filter((key) => config.settings.videoJSAllowedOptions.includes(key))
    .reduce((obj, key) => {
      obj[key] = options[key];
      return obj;
    }, {});
};

/**
 * Apple devices render subtitles from the HLS manifest natively and ignore
 * `addRemoteTextTrack()`, so VTT injection is impossible there. Any UI whose
 * behaviour depends on injection being possible must use this same condition.
 */
export const isAppleNativeHls = () =>
  videojs.browser.IS_ANY_SAFARI || videojs.browser.IS_IOS;

// Inject VTT text tracks into the player (non-Apple only)
const injectSubtitleTrack = (player, label, vttText) => {
  const remoteTrack = player.addRemoteTextTrack(
    { label, kind: 'subtitles' },
    true,
  );
  const textTrack = remoteTrack.track;
  const parser = new window.WebVTT.Parser(
    window,
    window.vttjs,
    window.WebVTT.StringDecoder(),
  );
  parser.oncue = (cue) => {
    cue.id = uuid();
    textTrack.addCue(cue);
  };
  parser.onparsingerror = (error) => {
    // eslint-disable-next-line no-console
    console.warn('VTT parse error:', error);
  };
  parser.parse(vttText);
  parser.flush();
};

/**
 * @param {Object} props
 * @param {Array<{src: string, type: string}>} props.sources
 * @param {Array<{label: string, vttText: string}>} [props.subtitleTracks]
 * @param {string[]} [props.storyboards]
 * @param {Object} [props.watermark]
 * @param {Object} [props.titleBar] - {title, href, linkTitle}; shows a linked
 *   title over the player. Embeds only -- the media page already has a heading.
 * @param {Object} [props.options] - VideoJS player options (filtered for safety)
 * @param {Function} [props.onReady] - Called with (player, hasSubtitles)
 * @param {Function} [props.onError]
 * @param {Function} [props.onXhrResponse]
 * @param {boolean} [props.selected]
 */
/**
 * Apply the sprite-thumbnail plugin, exactly once.
 *
 * `spriteThumbnails` is an *advanced* (class) videojs plugin: the first call
 * runs its constructor, and every later call returns the existing instance and
 * silently ignores the new options. So this must not be re-applied the way
 * `useSubtitleTracks` re-injects tracks — hence the guard ref rather than a
 * plain effect body.
 *
 * It also applies asynchronously: the constructor defers through
 * `player.ready()`, so `vjs-sprite-thumbnails` appears a tick later. Asserting
 * on the class synchronously reads as a failure when it is not one.
 */
const applyStoryboards = (player, urls, appliedRef) => {
  if (appliedRef.current) return;
  if (!player || !Array.isArray(urls) || urls.length === 0) return;

  const duration = player.duration();
  appliedRef.current = true;
  player.spriteThumbnails({
    urlArray: urls,
    width: 240,
    height: 135,
    columns: 3,
    rows: 3,
    interval: duration < 2 * 60 ? 5 : 10,
    // The plugin suppresses thumbnails below 1.5 Mbps by default. Our sprites
    // are ~20 KB webp fetched only on hover, so there is nothing to ration.
    downlink: 0,
  });
};

const VideoJS = (props) => {
  const videoRef = useRef(null);
  const playerRef = useRef(null);
  const metadataLoadedRef = useRef(false);

  const {
    sources,
    subtitleTracks,
    storyboards,
    watermark,
    titleBar,
    options,
    onReady,
    onError,
    onXhrResponse,
    selected,
  } = props;

  // Ref ensures loadedmetadata handler always sees latest tracks
  const subtitleTracksRef = useRef(subtitleTracks);
  subtitleTracksRef.current = subtitleTracks;

  // Same treatment for storyboards, and for the same reason. The player is
  // constructed as soon as a source exists, but the sprite URLs come from a
  // separate fetch that usually returns later — in dev by ~150ms, in
  // production by more, since that route presigns against OneFS while the
  // manifest comes from Wowza. Without the ref the `loadedmetadata` handler
  // closes over `storyboards` as it was at construction time, which is `null`,
  // and the thumbnails never appear at all.
  const storyboardsRef = useRef(storyboards);
  storyboardsRef.current = storyboards;
  const storyboardsAppliedRef = useRef(false);

  const filteredOptions = filterOptions(options);
  const hasSources = Array.isArray(sources) && sources.length > 0;

  // Create player when sources are available
  useEffect(() => {
    if (!hasSources || !videoRef.current) return;

    let videoElement;
    if (!playerRef.current) {
      videoElement = document.createElement('video-js');
      videoElement.className = 'video-js vjs-big-play-centered';
      videoElement.style.cssText = 'margin: auto; border-radius: 12px';
      videoRef.current.appendChild(videoElement);

      playerRef.current = videojs(
        videoElement,
        {
          ...filteredOptions,
          sources,
          language: 'fi',
          restoreEl: true,
          liveui: true,
          playsinline: true,
          html5: {
            // Start high and let measured segment throughput drive later ABR.
            // Browser downlink hints can be stale or heavily rounded, and
            // player dimensions must not cap the initial rendition.
            vhs: DEFAULT_VHS_OPTIONS,
          },
        },
        () => {
          const player = playerRef.current;
          const qualitySelector = player.hlsQualitySelector({
            displayCurrentQuality: true,
          });
          pinDefaultQuality(player, qualitySelector);
          videojs.addLanguage('fi', FI_TRANSLATIONS);

          // Linked title over the player (embeds only). The built-in TitleBar
          // renders plain text, so it is replaced -- in place, because its
          // position in the child list is what keeps the link ahead of the
          // controls in tab order. Set before 'loadedmetadata' so the title
          // shows over the poster, without waiting for play.
          if (titleBar?.title) {
            const builtIn = player.getChild('TitleBar');
            const index = builtIn
              ? player.children().indexOf(builtIn)
              : undefined;
            if (builtIn) {
              player.removeChild(builtIn);
              builtIn.dispose();
            }
            player
              .addChild(
                LINKED_TITLE_BAR,
                { href: titleBar.href, linkTitle: titleBar.linkTitle },
                index,
              )
              .update({ title: titleBar.title });
          }

          player.on('loadedmetadata', () => {
            metadataLoadedRef.current = true;

            // Storyboards (may still be in flight; the effect below covers that)
            applyStoryboards(
              player,
              storyboardsRef.current,
              storyboardsAppliedRef,
            );

            // Hotkeys
            player.hotkeys({ seekStep: 5 });

            // Watermark
            if (watermark?.enabled) {
              player.awesomeWatermark({
                fontSize: 100,
                fontColor: 'white',
                opacity: watermark.opacity,
                position: watermark.position,
                type: watermark.type,
                text: watermark.text,
                image: watermark.image
                  ? `data:${watermark.image['content-type']};${watermark.image.encoding},${watermark.image.data}`
                  : '',
              });
            }

            // Subtitle injection (non-Apple only)
            const isAppleNative = isAppleNativeHls();
            const tracks = Array.isArray(subtitleTracksRef.current)
              ? subtitleTracksRef.current
              : [];

            if (!isAppleNative && tracks.length > 0) {
              // Remove HLS manifest subtitle tracks
              const hlsTracks = Array.from(player.textTracks()).filter(
                (t) => t.kind === 'subtitles',
              );
              hlsTracks.forEach((track) => player.removeRemoteTextTrack(track));

              // Inject fetched VTT tracks
              for (const track of tracks) {
                if (track.vttText) {
                  injectSubtitleTrack(player, track.label, track.vttText);
                }
              }
            }

            if (onReady) onReady(player, tracks.length > 0);
          });

          player.on('error', () => {
            if (onError) onError(player);
          });

          if (onXhrResponse) {
            const tech = player.tech(true);
            if (tech?.vhs?.xhr) {
              tech.vhs.xhr.onResponse(onXhrResponse);
            }
          }
        },
      );
    }

    return () => {
      if (videoElement) {
        videoElement.remove();
      }
      if (playerRef.current) {
        playerRef.current.dispose();
        playerRef.current = null;
        metadataLoadedRef.current = false;
        storyboardsAppliedRef.current = false;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasSources]);

  // Handle storyboards arriving after the player was constructed.
  //
  // The losing condition for the old code was not "after metadata" but "after
  // the player was built", which is a much earlier deadline and was
  // effectively always missed. This effect is what actually fixes that; the
  // ref above only keeps the metadata handler honest when the data happens to
  // win the race.
  useEffect(() => {
    if (!metadataLoadedRef.current) return;
    applyStoryboards(playerRef.current, storyboards, storyboardsAppliedRef);
  }, [storyboards]);

  // Handle subtitle tracks arriving after metadata has already loaded
  useEffect(() => {
    const player = playerRef.current;
    if (!player || !metadataLoadedRef.current) return;

    const isAppleNative = isAppleNativeHls();
    if (isAppleNative) return;

    const tracks = Array.isArray(subtitleTracks) ? subtitleTracks : [];

    // Remove existing injected tracks and re-inject with current data
    const existingTracks = Array.from(player.textTracks()).filter(
      (t) => t.kind === 'subtitles',
    );
    // Re-injection replaces the track objects, and `addRemoteTextTrack` always
    // hands back a track in `disabled` mode. Carrying the previous mode over
    // is what keeps the subtitle editor's preview alive: without it the cues
    // update on every keystroke but nothing renders after the first one,
    // because only the initial onReady ever set `showing`.
    const showingLabels = new Set(
      existingTracks.filter((t) => t.mode === 'showing').map((t) => t.label),
    );
    existingTracks.forEach((track) => player.removeRemoteTextTrack(track));

    for (const track of tracks) {
      if (track.vttText) {
        injectSubtitleTrack(player, track.label, track.vttText);
      }
    }

    if (showingLabels.size > 0) {
      Array.from(player.textTracks()).forEach((t) => {
        if (t.kind === 'subtitles' && showingLabels.has(t.label)) {
          t.mode = 'showing';
        }
      });
    }
  }, [subtitleTracks]);

  return (
    <div data-vjs-player>
      <div
        ref={videoRef}
        className={cx({
          unselected: selected === false,
        })}
      />
    </div>
  );
};

export default VideoJS;
